import { v4 as uuid } from 'uuid'
import { OrderEntryStatusEnum } from './OrderEntryStatusEnum'
import { IVendorPriceListEntry } from './IVendorPriceListEntry'
import { Order } from '../Order'

export interface IOrderEntry {
  id: string
  priceListEntryId?: string
  upc: string
  description: string
  vendorCompanyId: string
  priceListId: string
  quantity: number
  receivedQuantity?: number
  additionalQuantity?: number
  pricePerUnit?: number
  status: OrderEntryStatusEnum
  dateAccepted?: Date
  shippingDate?: Date
  image?: string
  receivingNotes?: string
  orderingNotes?: string
  category?: string
  quantityType?: string
  receivedQuantityType?: string
  originalEntryId?: string // originating order entry for a backorder entry
}

export class OrderEntry implements IOrderEntry {
  public id: string = uuid()
  priceListEntryId?: string
  vendorCompanyId: string = ''
  priceListId: string = ''
  quantity: number = 0
  receivedQuantity?: number
  additionalQuantity?: number
  pricePerUnit?: number
  public status: OrderEntryStatusEnum = OrderEntryStatusEnum.PENDING
  dateAccepted?: Date
  shippingDate?: Date
  image?: string
  receivingNotes?: string = ''
  orderingNotes?: string = ''
  category?: string = ''
  quantityType?: string = ''
  receivedQuantityType?: string = ''
  originalEntryId?: string

  constructor(
      public upc: string,
      public description: string
  ) {}

  get isComplete() {
    return this.status === OrderEntryStatusEnum.ACCEPTED || this.status === OrderEntryStatusEnum.REJECTED
  }

  get isRejected() {
    return this.status === OrderEntryStatusEnum.REJECTED
  }

  get isPending() {
    return this.status === OrderEntryStatusEnum.PENDING || this.status === OrderEntryStatusEnum.SHIPPING
  }

  public get isBlank() {
    return !this.upc
      && !this.description
      && !this.vendorCompanyId
      && !this.priceListId
      && !this.receivedQuantity
      && !this.additionalQuantity
      && !this.pricePerUnit
      && !this.dateAccepted
      && !this.shippingDate
      && !this.image
      && !this.receivingNotes
      && !this.orderingNotes
      && !this.category
      && !this.quantityType
      && !this.receivedQuantityType
      && !this.originalEntryId
  }

  updateVendorDetails(update: IVendorPriceListEntry) {
    if (!update) {
      return
    }

    this.vendorCompanyId = update.vendorCompanyId
    this.priceListId = update.priceListId
    this.priceListEntryId = update.id
    this.upc = update.upc
    this.description = update.description
    this.image = update.barcode ? update.barcode.image : undefined
    this.orderingNotes = update.orderingNotes
    this.category = update.category
  }

  resetAcceptance(restaurantOrder?: Order) {
    this.status = OrderEntryStatusEnum.SHIPPING
    this.dateAccepted = undefined
    this.receivedQuantity = undefined
    this.receivedQuantityType = undefined
    this.pricePerUnit = undefined

    if (restaurantOrder) {
      const restaurantOrderEntry = this.lookupEquivalentRestaurantOrderEntry(restaurantOrder)
      if (restaurantOrderEntry) {
        restaurantOrderEntry.pricePerUnit = this.pricePerUnit
        restaurantOrderEntry.resetAcceptance()
      }
    }
  }

  accept(restaurantOrder?: Order) {
    this.status = OrderEntryStatusEnum.ACCEPTED
    this.dateAccepted = new Date()

    if (restaurantOrder) {
      const restaurantOrderEntry = this.lookupEquivalentRestaurantOrderEntry(restaurantOrder)
      if (restaurantOrderEntry) {
        restaurantOrderEntry.pricePerUnit = this.pricePerUnit
        restaurantOrderEntry.accept()
      }
    }
  }

  reject(restaurantOrder?: Order) {
    this.status = OrderEntryStatusEnum.REJECTED
    this.dateAccepted = undefined

    if (restaurantOrder) {
      const restaurantOrderEntry = this.lookupEquivalentRestaurantOrderEntry(restaurantOrder)
      if (restaurantOrderEntry) {
        restaurantOrderEntry.pricePerUnit = this.pricePerUnit
        restaurantOrderEntry.reject()
      }
    }
  }

  shipping(restaurantOrder?: Order | null) {
    this.status = OrderEntryStatusEnum.SHIPPING
    this.shippingDate = new Date()

    if (restaurantOrder) {
      const restaurantOrderEntry = this.lookupEquivalentRestaurantOrderEntry(restaurantOrder)
      if (restaurantOrderEntry) {
        restaurantOrderEntry.shipping()
      }
    }
  }

  protected lookupEquivalentRestaurantOrderEntry(restaurantOrder: Order): OrderEntry | undefined {
    return restaurantOrder.orderEntries.find((entry: OrderEntry) => entry.id === this.id)
  }
}
